import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/PersonAddAlt1';
import { adminService, AdminRecord } from '../services/adminService';

const Admins: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState<AdminRecord[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [uidInput, setUidInput] = useState('');
  const [setClaim, setSetClaim] = useState(true);
  const [removeClaim, setRemoveClaim] = useState(true);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState<string>('');
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error' | 'info'>('info');

  const sortedAdmins = useMemo(() => {
    return [...admins].sort((a, b) => {
      const ae = (a.email || '').toLowerCase();
      const be = (b.email || '').toLowerCase();
      if (ae && be) return ae.localeCompare(be);
      if (ae) return -1;
      if (be) return 1;
      return a.uid.localeCompare(b.uid);
    });
  }, [admins]);

  const showSnack = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const list = await adminService.listAdmins();
      setAdmins(list);
    } catch (e: any) {
      console.error('listAdmins error:', e);
      showSnack(e?.message || 'Erreur lors du chargement des admins', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const handleAddByEmail = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      showSnack('Email invalide.', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await adminService.addAdminByEmail(email, { setClaim });
      showSnack(`Admin ajouté: ${res.email} (${res.uid})`, 'success');
      setEmailInput('');
      await loadAdmins();
    } catch (e: any) {
      console.error('addAdminByEmail error:', e);
      showSnack(e?.message || 'Echec ajout admin par email', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddByUid = async () => {
    const uid = uidInput.trim();
    if (!uid) {
      showSnack('UID requis.', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await adminService.addAdminByUid(uid, { setClaim });
      showSnack(`Admin ajouté (uid): ${res.uid}`, 'success');
      setUidInput('');
      await loadAdmins();
    } catch (e: any) {
      console.error('addAdminByUid error:', e);
      showSnack(e?.message || 'Echec ajout admin par UID', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdmin = async (uid: string) => {
    if (!uid) return;
    setLoading(true);
    try {
      const res = await adminService.removeAdminByUid(uid, { removeClaim });
      showSnack(`Admin retiré: ${res.uid}`, 'success');
      await loadAdmins();
    } catch (e: any) {
      console.error('removeAdminByUid error:', e);
      showSnack(e?.message || 'Echec suppression admin', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Gestion des Administrateurs
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Ajoutez ou supprimez des administrateurs. Vous pouvez facultativement poser/retirer le custom claim admin=true.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Email"
            size="small"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
            placeholder="admin@example.com"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddByEmail}
            disabled={loading}
          >
            Ajouter par email
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="UID"
            size="small"
            value={uidInput}
            onChange={(e) => setUidInput(e.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
            placeholder="UID Firebase Auth"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddByUid}
            disabled={loading}
          >
            Ajouter par UID
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Switch checked={setClaim} onChange={(e) => setSetClaim(e.target.checked)} />}
            label="Poser le custom claim admin=true"
          />
          <FormControlLabel
            control={<Switch checked={removeClaim} onChange={(e) => setRemoveClaim(e.target.checked)} />}
            label="Retirer le custom claim lors de la suppression"
          />
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Rafraîchir">
            <span>
              <IconButton onClick={loadAdmins} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Liste des Admins</Typography>
          <Typography variant="body2" color="text.secondary">{sortedAdmins.length} admin(s)</Typography>
        </Stack>

        <Box sx={{ mt: 1 }}>
          {sortedAdmins.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Aucun administrateur trouvé.</Typography>
          ) : (
            sortedAdmins.map((a) => (
              <Stack
                key={a.uid}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{
                  py: 1,
                  borderBottom: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ wordBreak: 'break-all' }}>
                    {a.email || 'Email inconnu'} — {a.uid}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.createdAt ? `Ajouté le ${new Date(a.createdAt).toLocaleString()}` : 'Date inconnue'}
                    {a.createdBy ? ` — par ${a.createdBy}` : ''}
                  </Typography>
                </Box>
                <Tooltip title="Supprimer cet admin">
                  <span>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveAdmin(a.uid)}
                      disabled={loading}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))
          )}
        </Box>
      </Paper>

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)}>
        <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity} variant="filled" sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Admins;
